import type { PeopleView, StaffCapability, StaffGrant, StaffMemberView } from '@api/customers/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, ShieldCheck, Trash2, UserPlus, UserRound } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { acceptMyInvitation, allowStaff, disallowStaff, getMyInvitations, getPeople, inviteStaff, isStatus, removeStaff, resetStaffFactors, revokeStaffInvitation, setStaffAdmin,
  setStaffGrants } from '../lib/api';
import { useScope } from '../scope/scope';
import { useCatalog } from '../search/hooks';
import { Button } from '../ui/Button';
import { Card } from '../ui/misc';
import { useToast } from '../ui/toast';

const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';
export const capabilityLabels: Record<StaffCapability, string> = { 'devices.enroll': 'Add devices', 'diagnostics.run': 'Run runbooks',
  'scripts.run': 'Run PowerShell', 'jobs.approve': 'Approve jobs', 'remote.connect': 'Remote in', 'end_users.manage': 'Manage end users' };

/** Grants as one line: "All customers: Run runbooks, Remote in · Acme: Add devices". */
function grantsLine(grants: Array<StaffGrant & { organizationName?: string | null; siteName?: string | null }>, orgName: (id: string) => string | undefined) {
  return grants.map(g => `${g.organizationId ? g.organizationName ?? orgName(g.organizationId) ?? 'A customer' : 'All customers'}${g.siteName ? ` (${g.siteName})` : ''}: ${
    g.capabilities.length ? g.capabilities.map(c => capabilityLabels[c]).join(', ') : 'view only'}`).join(' · ');
}

/** Which customers, and what at each: only capabilities the person editing holds can be ticked. */
function GrantsEditor({ grants, onChange, holds }: { grants: StaffGrant[]; onChange: (next: StaffGrant[]) => void; holds: StaffCapability[] }) {
  const { catalog, siteName } = useCatalog();
  const set = (i: number, next: StaffGrant) => onChange(grants.map((g, j) => j === i ? next : g));
  // Site-scoped grants (from before) keep their site; they don't block choosing the whole customer too.
  const used = new Set(grants.filter(g => !g.siteId).map(g => g.organizationId ?? ''));
  const free = catalog.organizations.find(o => !used.has(o.id));
  return (
    <div className="grid gap-3">
      {grants.map((g, i) => (
        <fieldset key={i} className="grid gap-2 rounded-lg border border-line p-3">
          <div className="flex items-center gap-2">
            <select aria-label="Which customers" className={field} value={g.organizationId ?? ''} disabled={!!g.siteId}
              onChange={e => set(i, { ...g, organizationId: e.target.value || null })}>
              <option value="" disabled={used.has('') && g.organizationId !== null}>All customers</option>
              {catalog.organizations.map(o => <option key={o.id} value={o.id} disabled={used.has(o.id) && g.organizationId !== o.id}>{o.title}</option>)}
            </select>
            {g.siteId && <span className="shrink-0 text-[12.5px] text-ink-3">only {siteName(g.siteId) ?? 'one site'}</span>}
            {grants.length > 1 && <Button type="button" size="sm" variant="ghost" aria-label="Remove this grant" onClick={() => onChange(grants.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(Object.keys(capabilityLabels) as StaffCapability[]).map(c => (
              <label key={c} className={`flex items-center gap-1.5 text-[13px] ${holds.includes(c) ? '' : 'text-ink-3'}`}
                title={holds.includes(c) ? undefined : g.capabilities.includes(c) ? 'You don’t hold this: you can take it away, not keep or grant it' : 'You don’t hold this, so you can’t grant it'}>
                {/* Something the editor doesn't hold can be taken away (unticked), never ticked. */}
                <input type="checkbox" disabled={!holds.includes(c) && !g.capabilities.includes(c)} checked={g.capabilities.includes(c)}
                  onChange={e => set(i, { ...g, capabilities: e.target.checked ? [...g.capabilities, c] : g.capabilities.filter(x => x !== c) })} />
                {capabilityLabels[c]}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <Button type="button" size="sm" variant="ghost" className="justify-self-start" disabled={!free && used.has('')}
        onClick={() => onChange([...grants, { organizationId: free?.id ?? null, capabilities: [] }])}>
        Add a customer</Button>
    </div>
  );
}

/**
 * People (WRMM-133; Harry, 2026-10-12): the workspace's owners and admins invite technicians and admins, choose which
 * customers and capabilities each gets, keep the allowlist of who can be invited, and reset someone's 2FA. Nobody grants
 * more than they hold, changes their own access, or changes an owner.
 */
export function People() {
  const { mspId } = useScope();
  const { orgName } = useCatalog();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'people'];
  const people = useQuery({ queryKey: key, queryFn: ({ signal }) => getPeople(mspId, signal) });
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [admin, setAdmin] = useState(false);
  const [grants, setGrants] = useState<StaffGrant[]>([{ organizationId: null, capabilities: [] }]);
  const [editing, setEditing] = useState<{ personId: string; grants: StaffGrant[] } | null>(null);
  const [entry, setEntry] = useState('');
  const saved = (data: PeopleView) => client.setQueryData(key, data);
  const why = (error: unknown) => isStatus(error, 403) ? 'You can’t do that: not on the allowlist, more than you hold, your own access, or an owner.'
    : isStatus(error, 409) ? 'Not possible for this person (already a member, a customer user, or in another workspace: WITTCO resets those).'
    : isStatus(error, 400) ? 'Check the choices: an admin needs access to every customer.' : isStatus(error, 503) ? 'Email isn’t set up on this server yet.' : 'Try again.';
  const act = useMutation({ mutationFn: (run: () => Promise<PeopleView>) => run(),
    onSuccess: data => { saved(data); if (data.emailSent === false) toast({ tone: 'warning', title: 'Reset, but the setup email wasn’t sent', body: 'Reset 2FA again in a minute to send a new one.' }); },
    onError: error => toast({ tone: 'warning', title: 'That didn’t work', body: why(error) }) });
  const invite = useMutation({ mutationFn: () => inviteStaff(mspId, { email: email.trim(), admin, grants }),
    onSuccess: sent => {
      void client.invalidateQueries({ queryKey: key });
      toast(sent.emailSent === false ? { tone: 'warning', title: 'Invitation saved, but the email wasn’t sent', body: 'Revoke it and invite again in a minute.' }
        : { title: `Invitation sent to ${sent.email}` });
      setInviting(false); setEmail(''); setAdmin(false); setGrants([{ organizationId: null, capabilities: [] }]);
    },
    onError: error => toast({ tone: 'warning', title: 'Nobody was invited', body: why(error) }) });
  const data = people.data;
  if (people.isError) return <Card className="p-4"><p role="alert" className="text-[13px] text-attention">People couldn’t be loaded.</p></Card>;
  if (!data) return <Card className="p-4"><p className="text-[13px] text-ink-3">Loading…</p></Card>;
  const submit = (e: FormEvent) => { e.preventDefault(); invite.mutate(); };
  const confirmThen = (question: string, run: () => Promise<PeopleView>) => { if (window.confirm(question)) act.mutate(run); };
  const row = (m: StaffMemberView) => (
    <li key={m.personId} className="py-3">
      <div className="flex flex-wrap items-center gap-3">
        <UserRound className="size-4 text-ink-3" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{m.name ?? m.email ?? 'Someone without an email yet'}{m.you && <span className="ml-2 text-[12px] text-ink-3">you</span>}
            <span className="ml-2 rounded bg-sunken px-1.5 py-0.5 text-[11.5px] text-ink-2">{m.role === 'owner' ? 'Owner' : m.role === 'admin' ? 'Admin' : 'Technician'}</span></p>
          <p className="text-[12.5px] text-ink-3">{m.email ? `${m.email} · ` : ''}{grantsLine(m.grants, orgName)} · {m.keys || m.authenticator
            ? `2FA: ${[m.keys ? `${m.keys} key${m.keys === 1 ? '' : 's'}` : null, m.authenticator ? 'authenticator app' : null].filter(Boolean).join(', ')}` : 'no 2FA yet'}</p>
        </div>
        {!m.you && m.role !== 'owner' && <>
          <Button size="sm" variant="ghost" onClick={() => setEditing({ personId: m.personId, grants: m.grants.map(g => ({ organizationId: g.organizationId, siteId: g.siteId, capabilities: g.capabilities })) })}>Access</Button>
          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate(() => setStaffAdmin(mspId, m.personId, m.role !== 'admin'))}>{m.role === 'admin' ? 'Not admin' : 'Make admin'}</Button>
          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => confirmThen(`Reset ${m.email ?? 'their'} 2FA? Their keys, authenticator app and recovery codes stop working and they're signed out; with Google they set up a new one at their next sign-in, within 24 hours; with a password, a setup email goes to them.`, () => resetStaffFactors(mspId, m.personId))}>Reset 2FA</Button>
          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => confirmThen(`Remove ${m.email ?? 'them'} from this workspace?`, () => removeStaff(mspId, m.personId))}>Remove</Button>
        </>}
      </div>
      {editing?.personId === m.personId && (
        <div className="mt-3 grid gap-3">
          <GrantsEditor grants={editing.grants} holds={data.you.capabilities} onChange={next => setEditing({ personId: m.personId, grants: next })} />
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate(() => setStaffGrants(mspId, m.personId, editing.grants), { onSuccess: () => setEditing(null) })}>Save access</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </li>
  );
  return (
    <div className="grid gap-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="min-w-0 flex-1 text-[15px] font-semibold">Members</h3>
          {!inviting && <Button size="sm" onClick={() => setInviting(true)}><UserPlus className="size-4" /> Invite</Button>}
        </div>
        {inviting && (
          <form onSubmit={submit} className="mt-4 grid gap-3 rounded-lg border border-line p-4">
            <label className="text-[13px] font-medium">Email<input type="email" className={`${field} mt-1`} value={email} maxLength={254} onChange={e => setEmail(e.target.value)} /></label>
            <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={admin} onChange={e => setAdmin(e.target.checked)} />
              Admin: may also manage people (needs access to every customer)</label>
            <GrantsEditor grants={grants} onChange={setGrants} holds={data.you.capabilities} />
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={invite.isPending || !email.trim()}><Mail className="size-4" /> Send invitation</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setInviting(false)}>Cancel</Button>
            </div>
          </form>
        )}
        <ul className="mt-3 divide-y divide-line" aria-label="Members">{data.members.map(row)}</ul>
      </Card>
      <Card className="p-5">
        <h3 className="text-[15px] font-semibold">Open invitations</h3>
        <ul className="mt-2 divide-y divide-line" aria-label="Open invitations">
          {!data.invitations.length && <li className="py-2 text-[13px] text-ink-3">None.</li>}
          {data.invitations.map(i => (
            <li key={i.id} className="flex flex-wrap items-center gap-3 py-2">
              <p className="min-w-0 flex-1 text-[13.5px]"><span className="font-medium">{i.email}</span>{i.admin && ' · admin'}
                <span className="block text-[12.5px] text-ink-3">{grantsLine(i.grants, orgName)} · until {new Date(i.expiresAt).toLocaleDateString()}</span></p>
              <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate(() => revokeStaffInvitation(mspId, i.id))}>Revoke</Button>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-5">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold"><ShieldCheck className="size-4 text-ink-3" /> Who can be invited</h3>
        <p className="mt-1 text-[13px] text-ink-2">Addresses on this list, or in a domain on it. Add your own domain first; add a contractor by their address.</p>
        <ul className="mt-2 flex flex-wrap gap-2" aria-label="Allowlist">
          {data.allowlist.map(a => (
            <li key={a} className="flex items-center gap-1 rounded-full bg-sunken px-2.5 py-1 text-[13px]">{a}
              <button type="button" aria-label={`Remove ${a}`} className="text-ink-3 hover:text-ink" onClick={() => act.mutate(() => disallowStaff(mspId, a))}>×</button></li>
          ))}
        </ul>
        <form className="mt-3 flex gap-2" onSubmit={e => { e.preventDefault(); act.mutate(() => allowStaff(mspId, entry.trim()), { onSuccess: () => setEntry('') }); }}>
          <input aria-label="Domain or address" placeholder="example.com or someone@example.com" className={field} value={entry} maxLength={254} onChange={e => setEntry(e.target.value)} />
          <Button type="submit" size="sm" disabled={!entry.trim() || act.isPending}>Add</Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * Invitations to another workspace for someone already in one (WRMM-133): they join only when they accept here, so no
 * workspace adds an existing person (or sees their sign-ins) without their say.
 */
export function MyInvitations() {
  const client = useQueryClient();
  const toast = useToast();
  const invitations = useQuery({ queryKey: ['my-invitations'], queryFn: ({ signal }) => getMyInvitations(signal) });
  const accept = useMutation({ mutationFn: (id: string) => acceptMyInvitation(id),
    onSuccess: rest => { client.setQueryData(['my-invitations'], rest); void client.invalidateQueries({ queryKey: ['session'] });
      toast({ title: 'Invitation accepted', body: 'The workspace is in your workspace list.' }); },
    onError: () => toast({ tone: 'warning', title: 'Not accepted', body: 'It may have expired, been revoked, or its sender may no longer be able to grant it.' }) });
  if (!invitations.data?.length) return null;
  return (
    <Card className="divide-y divide-line">
      {invitations.data.map(i => (
        <div key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
          <Mail className="size-4 text-ink-3" />
          <p className="min-w-0 flex-1 text-[13.5px]">An invitation to <span className="font-medium">{i.workspace ?? 'a workspace'}</span>{i.from ? ` from ${i.from}` : ''}{i.admin ? ', as an admin' : ''}
            <span className="block text-[12.5px] text-ink-3">until {new Date(i.expiresAt).toLocaleDateString()}</span></p>
          <Button size="sm" variant="primary" disabled={accept.isPending} onClick={() => accept.mutate(i.id)}>Accept</Button>
        </div>
      ))}
    </Card>
  );
}
